import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { Menu, X, User, LogOut, Home } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicPage = ['/', '/login', '/signup'].includes(location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  if (!isPublicPage) return null;

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-700 bg-clip-text text-transparent">
              Bridge Care Ltd
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <div className="flex-shrink-0">
              <LanguageSwitcher />
            </div>
            {!user ? (
              <div className="flex items-center space-x-3 lg:space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-emerald-600 font-medium transition-colors whitespace-nowrap"
                >
                  {t('login')}
                </Link>
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-emerald-600 to-green-700 text-white px-4 lg:px-6 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
                >
                  {t('getStarted')}
                </Link>
              </div>
            ) : (
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-700 text-white px-4 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                >
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span className="max-w-32 truncate">{user.name || user.email}</span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name || user.email}</p>
                      <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                    </div>
                    <Link
                      to={`/${user.role}/dashboard`}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Home className="w-4 h-4 mr-3 flex-shrink-0" />
                      {t('dashboard')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <div>
                <LanguageSwitcher />
              </div>
              {!user ? (
                <div className="flex flex-col space-y-2 pt-2">
                  <Link
                    to="/login"
                    className="text-center text-gray-700 hover:text-green-600 font-medium py-2 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="text-center bg-gradient-to-r from-green-600 to-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('getStarted')}
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col space-y-2 pt-2">
                  <div className="px-4 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900">{user.name || user.email}</p>
                    <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                  </div>
                  <Link
                    to={`/${user.role}/dashboard`}
                    className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Home className="w-4 h-4" />
                    <span>{t('dashboard')}</span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center justify-center space-x-2 bg-red-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-red-700 transition-all"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('logout')}</span>
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
